import { Box, Button, Paper, Popper, Typography } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setOnboardingSpotlight } from '@redux/reducers/user.reducer';
import { RootState } from '@redux/store';
import { useTranslation } from '@hooks/useTranslation';

const OnboardingSpotlight: React.FC = () => {
  const dispatch = useDispatch();
  const spotlight = useSelector((state: RootState) => state.user.onboardingSpotlight);
  const localeTutorial = useTranslation('tutorial');

  const [targetElement, setTargetElement] = useState<HTMLElement | null>(null);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const popperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!spotlight) {
      setTargetElement(null);
      setTargetRect(null);
      return;
    }

    // Reset state when spotlight changes
    setTargetElement(null);
    setTargetRect(null);

    let currentElement: HTMLElement | null = null;
    let retryCount = 0;
    const maxRetries = 15;
    const retryInterval = 500;

    const findElement = () => {
      const element = document.querySelector(spotlight.targetSelector) as HTMLElement;
      if (element) {
        currentElement = element;

        // Elevate target element above the overlay
        const computedPosition = window.getComputedStyle(element).position;
        if (computedPosition === 'static') {
          element.style.position = 'relative';
        }
        element.style.zIndex = '9999';

        // Scroll element into view, then capture rect
        element.scrollIntoView({ behavior: 'auto', block: 'center', inline: 'center' });

        setTimeout(() => {
          requestAnimationFrame(() => {
            const rect = element.getBoundingClientRect();
            setTargetElement(element);
            setTargetRect(rect);
          });
        }, 100);
      } else {
        retryCount++;
        if (retryCount < maxRetries) {
          setTimeout(findElement, retryInterval);
        } else {
          console.warn(`OnboardingSpotlight: Target element not found: ${spotlight.targetSelector}`);
          // Auto-dismiss if element not found
          dispatch(setOnboardingSpotlight(null));
        }
      }
    };

    // Start searching after a short delay
    const timer = setTimeout(findElement, 300);

    return () => {
      clearTimeout(timer);
      if (currentElement) {
        const computedPosition = window.getComputedStyle(currentElement).position;
        if (currentElement.style.position === 'relative' && computedPosition === 'relative') {
          currentElement.style.position = '';
        }
        currentElement.style.zIndex = '';
      }
    };
  }, [spotlight, dispatch]);

  const handleDismiss = () => {
    dispatch(setOnboardingSpotlight(null));
  };

  if (!spotlight) {
    return null;
  }

  // Show loading overlay while searching
  if (!targetElement || !targetRect) {
    return (
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 9999,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      />
    );
  }

  const position = spotlight.position || 'right';

  return (
    <>
      {/* Dark backdrop overlay */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 9998,
          pointerEvents: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top overlay */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: `${targetRect.top - 8}px`,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
          }}
        />
        {/* Left overlay */}
        <Box
          sx={{
            position: 'absolute',
            top: `${targetRect.top - 8}px`,
            left: 0,
            width: `${targetRect.left - 8}px`,
            height: `${targetRect.height + 16}px`,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
          }}
        />
        {/* Right overlay */}
        <Box
          sx={{
            position: 'absolute',
            top: `${targetRect.top - 8}px`,
            left: `${targetRect.right + 8}px`,
            right: 0,
            height: `${targetRect.height + 16}px`,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
          }}
        />
        {/* Bottom overlay */}
        <Box
          sx={{
            position: 'absolute',
            top: `${targetRect.bottom + 8}px`,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
          }}
        />
        {/* Highlight border */}
        <Box
          sx={{
            position: 'absolute',
            top: `${targetRect.top - 8}px`,
            left: `${targetRect.left - 8}px`,
            width: `${targetRect.width + 16}px`,
            height: `${targetRect.height + 16}px`,
            border: '2px solid',
            borderColor: 'primary.main',
            borderRadius: '8px',
            boxShadow: '0 0 0 4px rgba(255, 255, 255, 0.2)',
            pointerEvents: 'none',
          }}
        />
      </Box>

      {/* Tooltip */}
      <Popper
        open={true}
        anchorEl={targetElement}
        placement={position as 'top' | 'bottom' | 'left' | 'right'}
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [0, 16],
            },
          },
        ]}
        sx={{ zIndex: 10000 }}
      >
        <Paper
          ref={popperRef}
          elevation={8}
          sx={{
            maxWidth: 400,
            p: 3,
            backgroundColor: 'white',
            borderRadius: 2,
            position: 'relative',
            zIndex: 10000,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5 }}>
            {spotlight.title}
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            {spotlight.content}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              onClick={handleDismiss}
              sx={{ textTransform: 'none' }}
            >
              {localeTutorial['gotIt'] || 'Got it'}
            </Button>
          </Box>
        </Paper>
      </Popper>
    </>
  );
};

export default OnboardingSpotlight;
